import React from 'react';
import { useIntl } from 'umi';
import { TableColumn, PagingQuery } from 'tea-component';
import CloumsCenter from '@/pages/intelligence/components/TableComponents/components/CloumsCenter';
import TableShowData from '@/pages/intelligence/components/TableComponents/components/TableShowData';
import SortBy, { SortByItemType } from '@/pages/intelligence/components/TableComponents/components/SortBy';
import { valueFormat, growthFormat, getActualWidthOfChars } from '@/pages/intelligence/utils/commonUtils';
import { sorter, sortTime } from '@/pages/intelligence/utils/sort';
import { DEFAULT_PAGE_SIZES } from '../constants';

import type { ICommonFlagMap } from '@/pages/intelligence/types/common';
import type { IStoreRankDetailsTablesSourceData, IValueAndRatioValue } from '@/pages/intelligence/types/storeRank';

export function getRecordsByPagination(
  records: IStoreRankDetailsTablesSourceData[],
  pagination: PagingQuery,
): IStoreRankDetailsTablesSourceData[] {
  if (!Array.isArray(records)) return [];
  const pageIndex = pagination?.pageIndex || 1;
  const pageSize = pagination?.pageSize || DEFAULT_PAGE_SIZES[0];
  const startIndex = (pageIndex - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  return records?.slice(startIndex, endIndex) || [];
}

export function getSortedRecords(
  sourceData: IStoreRankDetailsTablesSourceData[],
  sortInfo: SortByItemType,
): IStoreRankDetailsTablesSourceData[] {
  const { sortMetric, sortVal, ratioType } = sortInfo || {};
  const sourceDataTemp = [...sourceData];
  if (!sortMetric) return sourceDataTemp;
  let recordsTemp: IStoreRankDetailsTablesSourceData[] = [];
  if (sortMetric === 'date') {
    recordsTemp = sourceDataTemp.sort((a, b) => {
      const aDate = a?.date;
      const bDate = b?.date;
      return sortVal === 'asc' ? sortTime(bDate, aDate) : sortTime(aDate, bDate);
    });
  } else if (ratioType === 1) {
    recordsTemp = sourceDataTemp.sort((a, b) => {
      const aValue = (a?.[sortMetric] as IValueAndRatioValue)?.ratioValue;
      const bValue = (b?.[sortMetric] as IValueAndRatioValue)?.ratioValue;
      return sortVal === 'asc' ? sorter(aValue, bValue) : sorter(bValue, aValue);
    });
  } else {
    recordsTemp = sourceDataTemp.sort((a, b) => {
      const aValue = (a?.[sortMetric] as IValueAndRatioValue)?.value;
      const bValue = (b?.[sortMetric] as IValueAndRatioValue)?.value;
      return sortVal === 'asc' ? sorter(aValue, bValue) : sorter(bValue, aValue);
    });
  }
  return recordsTemp;
}

export function getDynamicColumns(option: {
  platformIcon?: (dimension: string) => string;
  platformIconTips?: (dimension: string) => React.ReactElement;
  dimensions: string[];
  dimensionFormatter?: (dimension: string) => string;
  entityType?: string;
  gameName?: string;
  gameCover?: string;
  dateType?: string;
  startDate?: string;
  endDate?: string;
  sortKey?: string;
  sortValue?: 'desc' | 'asc';
  onSortChage: (e: SortByItemType) => void;
}) {
  const { formatMessage } = useIntl();
  // 去重、过滤无效dimension
  const dimensions: string[] = (() => {
    const result: string[] = [];
    const map: ICommonFlagMap = {};
    const dimensionsTemp = option?.dimensions;
    if (!Array.isArray(dimensionsTemp)) return result;
    for (const dimensionTemp of dimensionsTemp) {
      if (!dimensionTemp) continue;
      if (map[dimensionTemp]) continue;
      result.push(dimensionTemp);
      map[dimensionTemp] = true;
    }
    return result;
  })();
  const sortKey = option?.sortKey || '';
  const sortValue = option?.sortValue || 'asc';
  const dimensionFormatter = option?.dimensionFormatter;
  const onSortChage = option?.onSortChage || (() => {});

  const columnsTemp: TableColumn<IStoreRankDetailsTablesSourceData>[] = [];
  const isOnlyOneDimension = dimensions?.length === 1;
  for (const dimension of dimensions) {
    const dimensionName = typeof dimensionFormatter === 'function' ? dimensionFormatter(dimension) : dimension;
    const columnInnerWidth = (() => {
      /**
       * 多列时，按照每列200的宽度来（包含左右padding20）即固定展示1-3、4-10、11-50、51-200 排名的数据
       * 单列时，按照实际表头文案的宽度来（包含左右padding20），即展示一个游戏的数据
       */
      let result =
        dimensions?.length > 1
          ? 200
          : // 40 是 td左右的padding、18是文本右侧排序icon的宽度、20 是预留的宽度（避免文本实际渲染宽度比计算的宽度多一点 导致出现...）
            getActualWidthOfChars(dimensionName, { fontSize: 14, fontWeight: 700 }) + 40 + 18 + 20;
      result = Math.max(result, 200);
      return result;
    })();
    const dimensionColumn: TableColumn<IStoreRankDetailsTablesSourceData> = {
      key: dimension,
      align: 'center',
      width: isOnlyOneDimension ? '50%' : columnInnerWidth,
      header: (
        <CloumsCenter width={columnInnerWidth} align="right">
          <SortBy
            title={dimensionName}
            svgIcon={option?.platformIcon?.(dimension)}
            svgTip={option?.platformIconTips?.(dimension)}
            list={[
              {
                key: `${dimension}_0`,
                ratioType: 0,
                sortMetric: dimension,
                text: dimensionName,
                title: dimensionName,
              },
              {
                key: `${dimension}_1`,
                ratioType: 1,
                sortMetric: dimension,
                text: `${dimensionName}:${formatMessage({ id: 'dbi_change' })}`,
                title: `${dimensionName}:${formatMessage({ id: 'dbi_change' })}`,
              },
            ]}
            defaultKey={sortKey}
            defaultValue={sortValue}
            onChange={onSortChage}
          />
        </CloumsCenter>
      ),
      render: (cvm: IStoreRankDetailsTablesSourceData) => {
        const dimensionValue = cvm?.[dimension] as IValueAndRatioValue;
        const value = valueFormat(dimensionValue?.value);
        const valueRatio = dimensionValue?.ratioValue;
        const growthDataObj = growthFormat({
          value: valueRatio,
          valueType: 'rankValue',
          hasPoint: 'none',
          fixedNum: 0,
          maxNumber: 9999,
        });

        return (
          <CloumsCenter width={columnInnerWidth} align="right">
            <TableShowData
              maxNumber="9999"
              value={value}
              // @ts-ignore
              type={growthDataObj?.type}
              growth={growthDataObj?.value}
            />
          </CloumsCenter>
        );
      },
    };
    columnsTemp.push(dimensionColumn);
  }
  return columnsTemp;
}
